import { AwardsService } from '../services';
import { MEMBERSHIP_STATUS_LABELS, NOT_ENTERED } from '../statuses';
import { awardsKeys } from '../queryKeys';
import { useOptimisticStatusMutation } from './useOptimisticStatusMutation';
import { useRowStatusEditor } from './useRowStatusEditor';

/**
 * Optimistically repaints the row, then rolls back if the server disagrees.
 * The select only offers legal transitions, so a rejection here means the row
 * moved underneath us — refetch to resync rather than trusting local state.
 *
 * Choosing not-entered removes the membership row server-side (it is the
 * absence of one), so the optimistic patch drops the row — and its slot in the
 * total — rather than painting a status no roster read can ever return.
 */
export const useUpdateMembershipStatus = (cycleId, options = {}) => {
  const mutation = useOptimisticStatusMutation(
    {
      mutationFn: ({ userId, status }) =>
        AwardsService.updateMembershipStatus(cycleId, userId, status),
      snapshotKey: awardsKeys.membershipsPrefix(cycleId),
      patchPage: (data, { userId, status }) => {
        if (status === NOT_ENTERED) {
          const items = data.items.filter((row) => row.userId !== userId);

          return items.length === data.items.length
            ? data
            : { ...data, items, totalCount: Math.max(0, data.totalCount - 1) };
        }

        return {
          ...data,
          items: data.items.map((row) =>
            row.userId === userId
              ? {
                ...row,
                status,
                statusLabel: MEMBERSHIP_STATUS_LABELS[status] ?? status,
              }
              : row,
          ),
        };
      },
      invalidateKeys: () => [awardsKeys.membershipsPrefix(cycleId)],
    },
    options,
  );

  return { ...mutation, ...useRowStatusEditor(mutation, 'userId') };
};
